// contexts/NotesContext.js - Refactored for workspace-level selection
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import * as notesApi from '../../api/notesApi';
import { usePanelContext } from './PanelContext';
import cacheService from '../../services/cacheService';
import indexedDBService from '../../services/indexedDBService';

const NotesContext = createContext();

export function NotesProvider({ children, selectedWorkspacePublicId }) {
  // ============================================================================
  // STATE
  // ============================================================================
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isSwapped } = usePanelContext();
  
  // ============================================================================
  // DEBOUNCED NOTE UPDATE
  // ============================================================================
  const debouncedNoteUpdate = useCallback(
    debounce(async (note) => {
      console.log('🔥 DEBOUNCED UPDATE FIRING for note:', note.id, 'display_mode:', note.display_mode);
      if (!selectedWorkspacePublicId) {
        console.warn('Attempted to update note without valid workspace ID');
        return;
      }

      try {
         console.log('📤 SENDING TO API - display_mode:', note.display_mode); 
        await notesApi.updateNote(selectedWorkspacePublicId, note.id, {
          content: note.content,
          title: note.title,
          type: note.type,
          image_url: note.image_url,
          is_minimized: note.is_minimized,
          x: note.x,
          y: note.y,
          main_x: note.main_x,
          main_y: note.main_y,
          main_width: note.main_width,
          main_height: note.main_height,
          display_mode: note.display_mode,
          list_order: note.list_order,
          zIndex: note.zIndex,
          tag1: note.tag1,
          tag2: note.tag2,
        });
      } catch (error) {
        console.error('Error updating note:', error);
        setError('Failed to update note');
      }
    }, 1000),
    [selectedWorkspacePublicId]
  );

  // ============================================================================
  // FETCH NOTES
  // ============================================================================
  const fetchNotes = useCallback(async (forceReload = false) => {
  if (!selectedWorkspacePublicId) {
    setNotes([]);
    setLoading(false);
    return;
  }
  
  try {
    setLoading(true);
    
    // Cache-first strategy for trusted devices
    if (cacheService.isTrustedDevice() && !forceReload) {
      console.log('📦 Loading notes from cache for workspace:', selectedWorkspacePublicId);
      
      // Load from cache first (instant)
      const cachedNotes = await indexedDBService.getNotesByWorkspace(selectedWorkspacePublicId);
      if (cachedNotes && cachedNotes.length > 0) {
        console.log(`✅ Loaded ${cachedNotes.length} notes from cache`);
        setNotes(cachedNotes);
        setLoading(false);
        setError(null);
        
        // Check if cache is stale (>5 minutes since last sync)
        const lastSyncTime = await indexedDBService.getLastSyncTime();
        const cacheAge = lastSyncTime ? Date.now() - new Date(lastSyncTime).getTime() : Infinity;
        const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
        
        if (cacheAge > STALE_THRESHOLD) {
          console.log('🔄 Notes cache is stale, refreshing in background...');
          notesApi.fetchNotes(selectedWorkspacePublicId).then(freshNotes => {
            // Update cache
            freshNotes.forEach(note => {
              indexedDBService.saveNote(note).catch(err => {
                console.error('Error caching note:', err);
              });
            });
            
            // Update state if changed
            if (JSON.stringify(cachedNotes) !== JSON.stringify(freshNotes)) {
              console.log('🔄 Notes changed, updating...');
              setNotes(freshNotes);
            }
          }).catch(err => {
            console.error('Background notes sync error:', err);
          });
        } else {
          console.log(`✅ Notes cache is fresh (synced ${Math.round(cacheAge / 1000)}s ago), skipping background sync`);
        }
        
        return;
      }
      
      // Cache is empty, fall through to API load
      console.log('📦 Notes cache empty, loading from API...');
    }
    
    // Fallback: Load from API (untrusted devices or empty cache)
    console.log('🌐 Loading notes from API...');
    const notesData = await notesApi.fetchNotes(selectedWorkspacePublicId);
    setNotes(notesData);
    setError(null);
    
    // Save to cache if trusted device
    if (cacheService.isTrustedDevice()) {
      console.log('💾 Caching notes...');
      for (const note of notesData) {
        await indexedDBService.saveNote(note).catch(err => {
          console.error('Error caching note:', err);
        });
      }
    }
    
  } catch (err) {
    console.error('Error loading notes:', err);
    setError('Failed to load notes');
  } finally {
    setLoading(false);
  }
}, [selectedWorkspacePublicId]);

  // ============================================================================
  // ADD NOTE
  // ============================================================================
  const addNote = useCallback(async (newZIndex, noteType) => {
    if (!selectedWorkspacePublicId) {
      console.warn('Attempted to add note without valid workspace ID');
      return;
    }

    const newNote = {
      content: 'New Note',
      title: null,
      x: 20,
      y: 100,
      width: 150,
      height: 150,
      zIndex: newZIndex,
      type: noteType
    };

    try {
      const createdNote = await notesApi.createNote(selectedWorkspacePublicId, newNote);
      setNotes(prev => [...prev, createdNote]);
      return createdNote.id;
    } catch (error) {
      console.error('Error creating note:', error);
      setError('Failed to create note');
    }
  }, [selectedWorkspacePublicId]);

  // ============================================================================
  // UPDATE NOTE
  // ============================================================================
  const updateNote = useCallback(async (id, updates) => {
    if (!selectedWorkspacePublicId) {
      console.warn('Attempted to update note without valid workspace ID');
      return;
    }
  
    try {
      // Determine if this is a main panel update based on properties and swap state
      const hasContentProps = ['content', 'title'].some(prop => prop in updates);

      // Determine if this is ONLY a main panel layout update
      const isMainPanelUpdate = isSwapped 
        ? ['x', 'y', 'width', 'height'].some(prop => prop in updates) && !hasContentProps
        : ['main_x', 'main_y', 'main_width', 'main_height'].some(prop => prop in updates) && !hasContentProps;
      
      setNotes(prevNotes => {
        const updatedNotes = prevNotes.map(note => {
          if (note.id !== id) return note;
          
          // Always update all properties that are provided
          const updatedNote = {
            ...note,
            // Main panel properties
            main_x: updates.main_x !== undefined ? updates.main_x : note.main_x,
            main_y: updates.main_y !== undefined ? updates.main_y : note.main_y,
            main_width: updates.main_width !== undefined ? updates.main_width : note.main_width,
            main_height: updates.main_height !== undefined ? updates.main_height : note.main_height,
            
            // Right panel properties
            x: updates.x !== undefined ? updates.x : note.x,
            y: updates.y !== undefined ? updates.y : note.y,
            width: updates.width !== undefined ? updates.width : note.width,
            height: updates.height !== undefined ? updates.height : note.height,
            
            // Common properties
            content: updates.content !== undefined ? updates.content : note.content,
            title: updates.title !== undefined ? updates.title : note.title,
            zIndex: updates.zIndex !== undefined ? updates.zIndex : note.zIndex,
            is_minimized: updates.is_minimized !== undefined ? updates.is_minimized : note.is_minimized,
            display_mode: updates.display_mode !== undefined ? updates.display_mode : note.display_mode,
            tag1: updates.tag1 !== undefined ? updates.tag1 : note.tag1,
            tag2: updates.tag2 !== undefined ? updates.tag2 : note.tag2,
            type: updates.type !== undefined ? updates.type : note.type,
            image_url: updates.image_url !== undefined ? updates.image_url : note.image_url
          };
  
          // Trigger debounced API update
          // ✅ FIX: Save display_mode immediately, debounce everything else
              if (updates.display_mode !== undefined) {
                  console.log('💾 IMMEDIATE SAVE for display_mode:', id, updates.display_mode);
                  notesApi.updateNote(selectedWorkspacePublicId, id, {
                    display_mode: updates.display_mode
                  }).catch(err => console.error('Failed to save display_mode:', err));
                }

                // ALWAYS call debounced update (removed the "else")
                debouncedNoteUpdate(updatedNote);
  
          return updatedNote;
        });
  
        return updatedNotes;
      });
    } catch (error) {
      console.error('Error updating note:', error);
      setError('Failed to update note');
    }
  }, [selectedWorkspacePublicId, debouncedNoteUpdate, isSwapped]);

  // ============================================================================
  // REMOVE NOTE
  // ============================================================================
  const removeNote = useCallback(async (id) => {
    if (!selectedWorkspacePublicId) {
      console.warn('Attempted to remove note without valid workspace ID');
      return;
    }

    try {
      const result = await notesApi.deleteNote(selectedWorkspacePublicId, id);
      
      setNotes(prevNotes => {
        const filtered = prevNotes.filter(note => Number(note.id) !== Number(id));
        return filtered;
      });
      
    } catch (error) {
      console.error(`Error removing note ${id}:`, error);
      setError('Failed to remove note');
    }
  }, [selectedWorkspacePublicId]);

  // ============================================================================
  // SAVE NOTE LAYOUT
  // ============================================================================
  const saveNoteLayout = useCallback(async (layoutUpdates) => {
    if (!selectedWorkspacePublicId) {
      console.warn('Attempted to save layout without valid workspace ID');
      return;
    }

    try {
      await notesApi.updateNoteLayouts(selectedWorkspacePublicId, layoutUpdates);
    } catch (error) {
      console.error('Error saving layout:', error);
      setError('Failed to save layout');
    }
  }, [selectedWorkspacePublicId]);

  // ============================================================================
  // REORDER NOTES
  // ============================================================================
  const reorderNotes = useCallback(async (orderUpdates) => {
    if (!selectedWorkspacePublicId) return;
    
    try {
      // Update local state immediately for responsive UI
      setNotes(prevNotes => {
        return prevNotes.map(note => {
          const update = orderUpdates.find(u => u.id === note.id);
          return update ? { ...note, list_order: update.list_order } : note;
        });
      });

      // Update each note via API
      for (const update of orderUpdates) {
        const updatePayload = {
          list_order: update.list_order
        };
        
        await notesApi.updateNote(selectedWorkspacePublicId, update.id, updatePayload);
      }
      
      console.log('✅ Successfully reordered notes');
    } catch (error) {
      console.error('❌ Error reordering notes:', error);
      setError('Failed to reorder notes');
      // Rollback on error
      fetchNotes(true);
    }
  }, [selectedWorkspacePublicId, fetchNotes]);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  // Load notes whenever the workspace ID changes
  useEffect(() => {
    if (selectedWorkspacePublicId) {
      fetchNotes();
    } else {
      // No workspace selected - clear notes
      setNotes([]);
      setLoading(false);
    }
  }, [selectedWorkspacePublicId, fetchNotes]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedNoteUpdate.cancel();
    };
  }, [debouncedNoteUpdate]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  const value = {
    notes,
    loading,
    error,
    fetchNotes,
    addNote,
    updateNote,
    removeNote,
    saveNoteLayout,
    reorderNotes,
    workspacePublicId: selectedWorkspacePublicId
  };

  return (
    <NotesContext.Provider value={value}>
      {children}
    </NotesContext.Provider>
  );
}

export function useNotes() {
  const context = useContext(NotesContext);
  if (!context) {
    throw new Error('useNotes must be used within a NotesProvider');
  }
  return context;
}